import { useId } from 'react';

import { cn } from '@/shared/utils/cn/cn.util';

import { FormLegendLabel } from '../FormLegendLabel/FormLegendLabel';
import {
  FORM_LEGEND_CLASSES,
  FORM_SECTION_CLASSES,
  FORM_SECTION_DESCRIPTION_CLASSES,
} from '../dynamic-form.tokens';

import { type FormFieldsetProps } from './FormFieldset.types';

/**
 * A named group of controls — the kit's ONE `<fieldset>`.
 *
 * Extracted in P10.3b, when the object field became a second thing that needed
 * one. Two copies of "the `<legend>` is the fieldset's first child" is how one
 * of them silently stops being true, and the failure is invisible: the group
 * still looks right and simply stops having a name.
 *
 * Three properties it holds for every caller:
 *
 * 1. **The legend is the first child**, always. A legend nested inside anything
 *    is an ordinary element that happens to be at the top of a box.
 * 2. **The UA reset lives in the tokens.** A browser gives every fieldset a
 *    border, an inline margin and a legend its own padding; `border-0 p-0` in
 *    `FORM_SECTION_CLASSES` is what stops the portal looking like a 1997 form,
 *    written logically so RTL is unaffected.
 * 3. **`aria-describedby` is present only when there is something to point at.**
 *
 * The **collapsible** section is deliberately NOT built on this: its Collapsible
 * root has to BE the fieldset (`asChild`) for the legend to stay first, so it
 * composes `FormLegendLabel` instead and keeps its own element.
 */
export function FormFieldset({
  id,
  title,
  description,
  isInvalid = false,
  className,
  children,
}: FormFieldsetProps) {
  const generatedId = useId();
  const descriptionId = `${generatedId}-description`;

  return (
    <fieldset
      id={id ?? generatedId}
      aria-describedby={description === undefined ? undefined : descriptionId}
      className={cn(FORM_SECTION_CLASSES, className)}
    >
      <legend className={FORM_LEGEND_CLASSES}>
        <FormLegendLabel title={title} isInvalid={isInvalid} />
      </legend>
      {description === undefined ? null : (
        <p id={descriptionId} className={FORM_SECTION_DESCRIPTION_CLASSES}>
          {description}
        </p>
      )}
      {children}
    </fieldset>
  );
}
